import { useEffect, useRef } from "react";

/**
 * Viseme-driven mouth canvas overlay.
 *
 * Positioned absolutely over a face image (parent must be relative + 4:5 portrait).
 * Reads two globals set by the caller's SpeechSynthesisUtterance onboundary handler:
 *   - window.__opikSpeechWord     — incremented per word boundary
 *   - window.__opikSpeechViseme   — {open: 0-1, wide: 0-1} for the current word
 * (See `pickViseme()` in GemologistAssistant.jsx.)
 *
 * Smoothly interpolates between viseme targets so the mouth doesn't snap. When
 * `speaking` is false, the mouth decays to closed.
 */
const MouthCanvas = ({ speaking }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({ open: 0, wide: 0.5, targetOpen: 0, targetWide: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const cx = W * 0.5;
    const cy = H * 0.62;
    const baseRX = W * 0.045;
    const baseRY = H * 0.018;
    let lastWord = -2;

    const draw = (t) => {
      ctx.clearRect(0, 0, W, H);
      if (speaking) {
        const curWord = window.__opikSpeechWord ?? -1;
        if (curWord !== lastWord && window.__opikSpeechViseme) {
          stateRef.current.targetOpen = window.__opikSpeechViseme.open;
          stateRef.current.targetWide = window.__opikSpeechViseme.wide;
          lastWord = curWord;
        }
        const jitterOpen = 0.10 * Math.sin(t / 75);
        const jitterWide = 0.05 * Math.sin(t / 130);
        const s = stateRef.current;
        s.open += (s.targetOpen + jitterOpen - s.open) * 0.25;
        s.wide += (s.targetWide + jitterWide - s.wide) * 0.22;
        const rx = baseRX * (0.7 + s.wide * 0.7);
        const ry = baseRY * (0.1 + Math.max(0, s.open) * 1.4);
        if (ry > 0.4) {
          ctx.fillStyle = "rgba(55, 28, 22, 0.92)";
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "rgba(20, 8, 5, 0.95)";
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx * 0.7, ry * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        const s = stateRef.current;
        s.open += (0 - s.open) * 0.18;
        s.wide += (0.5 - s.wide) * 0.18;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [speaking]);

  return (
    <canvas
      ref={canvasRef}
      width={640}
      height={800}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
};

export default MouthCanvas;
